import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  TruckOutlined,
  ClockCircleOutlined,
  ShopOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CalendarOutlined,
  CloseOutlined,
  ReloadOutlined,
  LeftOutlined,
  RightOutlined,
  LoadingOutlined,
  InboxOutlined,
  PictureOutlined,
  ZoomInOutlined,
} from "@ant-design/icons";
import axios from "../../utils/axiosDash";

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------
const STORE_STORAGE_KEY = "selectedStore"; // import yours if it already exists

const getStoreName = () => {
  try {
    return localStorage.getItem(STORE_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

// ---------------------------------------------------------------------------
// Data normalisation (matches the empireMaterialInwards response)
// ---------------------------------------------------------------------------
// Fields from the API:
//   _id, restaurant, postgresId, anprNumber (can be null), imageUrl, status,
//   truckArrivalTimestamp, unloadingStartTimestamp, unloadingEndTimestamp,
//   unloadingDurationSeconds, postgresCreatedAt, postgresUpdatedAt
// The record time comes ONLY from `postgresCreatedAt`.

const toDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const normalizeStatus = (raw) => {
  const s = String(raw ?? "").toLowerCase();
  if (!s) return "unknown";
  if (s.includes("complete") || s.includes("done") || s.includes("finish") || s === "unloaded") return "completed";
  if (s.includes("unload") || s.includes("progress") || s.includes("active")) return "unloading";
  if (s.includes("arriv") || s.includes("pending") || s.includes("waiting")) return "arrived";
  if (s.includes("divert") || s.includes("exception") || s.includes("fail")) return "exception";
  return "unknown";
};

const formatSeconds = (sec) => {
  if (sec === null || sec === undefined || sec === "" || Number.isNaN(Number(sec))) return null;
  const total = Math.round(Number(sec));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const normalize = (r) => {
  const status = normalizeStatus(r.status);
  const durationText = formatSeconds(r.unloadingDurationSeconds);

  return {
    id: String(r._id ?? r.postgresId ?? ""),
    postgresId: r.postgresId ?? null,
    // ONLY postgresCreatedAt is used as the record time
    time: toDate(r.postgresCreatedAt),
    store: r.restaurant || "—",
    plate: r.anprNumber || null, // ANPR can be null
    status,
    rawStatus: r.status || "—",
    arrival: toDate(r.truckArrivalTimestamp),
    unloadStart: toDate(r.unloadingStartTimestamp),
    unloadEnd: toDate(r.unloadingEndTimestamp),
    duration: durationText || (status === "unloading" ? "In progress" : "—"),
    image: r.imageUrl || null,
    raw: r,
  };
};

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------
const pad = (n) => String(n).padStart(2, "0");
const toInputDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const shiftDate = (str, days) => {
  const [y, m, d] = str.split("-").map(Number);
  return toInputDate(new Date(y, m - 1, d + days));
};

// ALL timestamps are shown exactly as stored (timeZone: "UTC" => NO +5:30 / -5:30 shift).
const fmtDateTime = (d) =>
  d
    ? d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "UTC",
      })
    : "—";

const fmtDateTimeAsIs = fmtDateTime;

const fmtTime = (d) =>
  d
    ? d.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
        timeZone: "UTC",
      })
    : "—";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const STATUS_STYLES = {
  completed: { label: "Completed", dot: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50" },
  unloading: { label: "Unloading", dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50" },
  arrived: { label: "Arrived", dot: "bg-sky-500", text: "text-sky-700", bg: "bg-sky-50" },
  exception: { label: "Exception", dot: "bg-rose-500", text: "text-rose-700", bg: "bg-rose-50" },
  unknown: { label: "Recorded", dot: "bg-slate-400", text: "text-slate-600", bg: "bg-slate-100" },
};

function StatCard({ icon, label, value, sub, accent }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg ${accent}`}>{icon}</div>
      <div className="min-w-0">
        <p className="text-2xl font-semibold leading-tight text-slate-800">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
        {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.unknown;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${s.bg} ${s.text}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

// Image with graceful fallback when missing / broken
function SnapshotImage({ src, alt, className = "", imgClassName = "object-cover" }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-1 bg-gradient-to-br from-slate-100 to-slate-200 text-slate-400 ${className}`}
      >
        <PictureOutlined className="text-lg" />
        <span className="text-[10px]">No image</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`${className} ${imgClassName}`}
    />
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <dt className="text-xs text-slate-400">{label}</dt>
      <dd className="text-slate-700">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function MaterialInward() {
  const [store, setStore] = useState(localStorage.getItem(STORE_STORAGE_KEY) || "All Stores");
  const [date, setDate] = useState(toInputDate(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const sync = () => {
      const next = localStorage.getItem(STORE_STORAGE_KEY) || "All Stores";
      setStore((prev) => (prev !== next ? next : prev));
    };
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  const fetchData = useCallback(async () => {
    if (!store) {
      setEvents([]);
      setError("No store selected. Please select a store first.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await axios.post("/get-empire-material-inwards", { store, date });
      const json = res?.data || {};
      if (!json.success) throw new Error(json.message || "Request failed");

      const list = (json.data || []).map(normalize);
      // newest first (by postgresCreatedAt)
      list.sort((a, b) => (b.time?.getTime() || 0) - (a.time?.getTime() || 0));
      setEvents(list);
    } catch (e) {
      setEvents([]);
      setError(e?.response?.data?.message || e.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, [store, date]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Close modal with Escape
  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => e.key === "Escape" && setSelected(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events.filter(
      (e) =>
        !q ||
        String(e.plate || "").toLowerCase().includes(q) ||
        String(e.id).toLowerCase().includes(q) ||
        String(e.postgresId ?? "").toLowerCase().includes(q) ||
        String(e.store).toLowerCase().includes(q)
    );
  }, [events, query]);

  const arrived = events.filter((e) => e.status === "arrived").length;
  const unloading = events.filter((e) => e.status === "unloading").length;
  const completed = events.filter((e) => e.status === "completed").length;

  const isToday = date === toInputDate(new Date());
  const dateLabel = new Date(`${date}T00:00:00`).toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="min-h-screen w-full bg-slate-50 text-slate-800">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Material Inward</h1>
            <p className="mt-1 text-sm text-slate-500">
              Truck arrivals, unloading activity and CCTV snapshots for the selected day.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            {/* Date filter */}
            <div className="flex items-center overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <button
                onClick={() => setDate(shiftDate(date, -1))}
                className="px-2.5 py-2 text-slate-500 hover:bg-slate-50 hover:text-teal-700"
                title="Previous day"
              >
                <LeftOutlined />
              </button>
              <div className="relative border-x border-slate-100">
                <CalendarOutlined className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="date"
                  value={date}
                  max={toInputDate(new Date())}
                  onChange={(e) => e.target.value && setDate(e.target.value)}
                  className="bg-transparent py-2 pl-9 pr-3 text-sm text-slate-700 focus:outline-none"
                />
              </div>
              <button
                onClick={() => setDate(shiftDate(date, 1))}
                disabled={isToday}
                className="px-2.5 py-2 text-slate-500 hover:bg-slate-50 hover:text-teal-700 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent"
                title="Next day"
              >
                <RightOutlined />
              </button>
            </div>

            {!isToday && (
              <button
                onClick={() => setDate(toInputDate(new Date()))}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 shadow-sm hover:bg-slate-50"
              >
                Today
              </button>
            )}

            <button
              onClick={fetchData}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-700 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-800 disabled:opacity-60"
            >
              {loading ? <LoadingOutlined /> : <ReloadOutlined />}
              Refresh
            </button>
          </div>
        </div>

        <p className="mb-4 text-sm text-slate-500">
          Showing <span className="font-medium text-slate-700">{store || "—"}</span> · {dateLabel}
        </p>

        {/* Error banner */}
        {error && (
          <div className="mb-6 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">
            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
              <WarningOutlined />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-red-700">Couldn't load material inwards</p>
              <p className="mt-0.5 text-xs text-red-500">{error}</p>
            </div>
            <button
              onClick={fetchData}
              className="shrink-0 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={<TruckOutlined />}
            label="Truck arrivals"
            value={events.length}
            sub="Total for the day"
            accent="bg-teal-50 text-teal-700"
          />
          <StatCard
            icon={<ClockCircleOutlined />}
            label="Arrived"
            value={arrived}
            sub="Waiting to unload"
            accent="bg-sky-50 text-sky-700"
          />
          <StatCard
            icon={<PlayCircleOutlined />}
            label="Unloading"
            value={unloading}
            sub="Currently in progress"
            accent="bg-amber-50 text-amber-700"
          />
          <StatCard
            icon={<CheckCircleOutlined />}
            label="Completed"
            value={completed}
            sub="Unloading finished"
            accent="bg-emerald-50 text-emerald-700"
          />
        </div>

        {/* Table */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-sm font-semibold text-slate-700">
                Inward activity history{" "}
                <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
                  {filtered.length}
                </span>
              </h2>
              <div className="relative">
                <SearchOutlined className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search plate number, ID..."
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-700 placeholder-slate-400 focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 sm:w-64"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-3 font-medium">Snapshot</th>
                  <th className="px-4 py-3 font-medium">Date / Time</th>
                  <th className="px-4 py-3 font-medium">Store</th>
                  <th className="px-4 py-3 font-medium">Vehicle (ANPR)</th>
                  <th className="px-4 py-3 font-medium">Unloading started</th>
                  <th className="px-4 py-3 font-medium">Duration</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-400">
                      <LoadingOutlined className="mr-2" />
                      Loading material inwards…
                    </td>
                  </tr>
                )}

                {!loading &&
                  filtered.map((e) => (
                    <tr key={e.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                      {/* Thumbnail */}
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setSelected(e)}
                          title="Click to view image"
                          className="group relative block h-14 w-24 overflow-hidden rounded-lg border border-slate-200 bg-slate-100"
                        >
                          <SnapshotImage src={e.image} alt={e.plate || "Truck"} className="h-full w-full" />
                          {e.image && (
                            <span className="absolute inset-0 flex items-center justify-center bg-slate-900/0 text-white opacity-0 transition group-hover:bg-slate-900/40 group-hover:opacity-100">
                              <ZoomInOutlined />
                            </span>
                          )}
                        </button>
                      </td>

                      {/* postgresCreatedAt shown as-is (no timezone shift) */}
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                        <div className="flex items-center gap-2">
                          <CalendarOutlined className="text-slate-300" />
                          {fmtDateTimeAsIs(e.time)}
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                        <div className="flex items-center gap-2">
                          <ShopOutlined className="text-slate-300" />
                          {e.store}
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center gap-2">
                          <TruckOutlined className="text-teal-600" />
                          {e.plate ? (
                            <span className="rounded-md border border-slate-300 bg-slate-50 px-2 py-0.5 font-mono text-xs font-semibold tracking-wider text-slate-800">
                              {e.plate}
                            </span>
                          ) : (
                            <span className="text-xs italic text-slate-400">Plate not detected</span>
                          )}
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">{fmtTime(e.unloadStart)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">{e.duration}</td>
                      <td className="px-4 py-3">
                        <StatusPill status={e.status} />
                      </td>
                    </tr>
                  ))}

                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-14 text-center text-sm text-slate-400">
                      <InboxOutlined className="mb-2 block text-3xl text-slate-300" />
                      {events.length === 0
                        ? "No material inward records for this store and date."
                        : "No inward events match your filters."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Image / detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 p-4">
              <h3 className="text-sm font-semibold text-slate-800">
                {selected.plate ? selected.plate : "Truck arrival"} — Snapshot
              </h3>
              <button
                onClick={() => setSelected(null)}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-50 hover:text-slate-600"
                title="Close"
              >
                <CloseOutlined />
              </button>
            </div>

            <div className="p-4">
              <div className="mb-4 overflow-hidden rounded-xl bg-slate-900">
                <SnapshotImage
                  src={selected.image}
                  alt={selected.plate || "Truck snapshot"}
                  className="max-h-[60vh] min-h-[200px] w-full"
                  imgClassName="object-contain"
                />
              </div>

              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
                <Detail label="Vehicle (ANPR)">{selected.plate || "Not detected"}</Detail>
                <Detail label="Store">{selected.store}</Detail>
                <Detail label="Status">
                  <span className="mt-0.5 inline-block">
                    <StatusPill status={selected.status} />
                  </span>
                </Detail>
                <Detail label="Recorded at">{fmtDateTimeAsIs(selected.time)}</Detail>
                <Detail label="Truck arrived">{fmtDateTime(selected.arrival)}</Detail>
                <Detail label="Duration">{selected.duration}</Detail>
                <Detail label="Unloading started">{fmtDateTime(selected.unloadStart)}</Detail>
                <Detail label="Unloading ended">{fmtDateTime(selected.unloadEnd)}</Detail>
              </dl>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}